/**
 * Hook useSubscription
 * Hook personalizado para gestionar suscripciones de usuarios
 * Actualizado para usar funciones RPC optimizadas
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  getUserActiveSubscription, 
  getSubscriptionWithPayments,
  hasActiveSubscription,
  getSubscriptionStatus,
  formatSubscriptionForComponent
} from '../api/subscription';
import { ERROR_MESSAGES } from '../constants/subscriptions';

/**
 * Hook para gestionar suscripciones
 * @param {string} userId - ID del usuario
 * @param {Object} options - Opciones adicionales
 */
export const useSubscription = (userId, options = {}) => {
  const { autoRefresh = true, refreshInterval = 30000 } = options;
  
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  /**
   * Carga la suscripción del usuario usando RPC optimizado
   */
  const loadSubscription = useCallback(async () => {
    if (!userId) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Usar la nueva función RPC optimizada
      const result = await getSubscriptionWithPayments(userId);
      
      if (result) {
        // Formatear datos para compatibilidad con el componente
        const formattedSubscription = formatSubscriptionForComponent(result);
        setSubscription(formattedSubscription);
        setLastUpdated(new Date());
      } else {
        setSubscription(null);
      }
    } catch (err) {
      setError(err.message || ERROR_MESSAGES.GENERIC_ERROR);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  /**
   * Refresca la suscripción
   */
  const refreshSubscription = useCallback(async () => {
    await loadSubscription();
  }, [loadSubscription]);

  /**
   * Cancela la suscripción (placeholder - requiere implementación)
   */
  const cancelSubscription = useCallback(async (reason = '') => {
    // TODO: Implementar usando RPC cuando esté disponible
    console.warn('cancelSubscription requiere implementación con RPC');
    return { success: false, error: 'Función no implementada' };
  }, []);

  /**
   * Pausa la suscripción (placeholder - requiere implementación)
   */
  const pauseSubscription = useCallback(async () => {
    // TODO: Implementar usando RPC cuando esté disponible
    console.warn('pauseSubscription requiere implementación con RPC');
    return { success: false, error: 'Función no implementada' };
  }, []);

  /**
   * Reactiva la suscripción (placeholder - requiere implementación)
   */
  const reactivateSubscription = useCallback(async () => {
    // TODO: Implementar usando RPC cuando esté disponible
    console.warn('reactivateSubscription requiere implementación con RPC');
    return { success: false, error: 'Función no implementada' };
  }, []);

  /**
   * Sincroniza con Mercado Pago (placeholder - requiere implementación)
   */
  const syncWithMercadoPago = useCallback(async () => {
    // TODO: Implementar usando RPC cuando esté disponible
    console.warn('syncWithMercadoPago requiere implementación con RPC');
    return { success: false, error: 'Función no implementada' };
  }, []);

  /**
   * Obtiene el historial de pagos usando datos ya cargados
   */
  const getPaymentHistory = useCallback(async (page = 1, limit = 10) => {
    if (!subscription || !subscription.pagos_suscripciones) {
      return { success: false, error: 'No hay pagos disponibles' };
    }

    try {
      // Los pagos ya vienen incluidos en la suscripción
      const payments = subscription.pagos_suscripciones;
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedPayments = payments.slice(startIndex, endIndex);
      
      return { 
        success: true, 
        payments: paginatedPayments,
        total: payments.length,
        page,
        limit,
        hasMore: endIndex < payments.length
      };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [subscription]);

  /**
   * Verifica si la suscripción está activa
   */
  const isActive = subscription?.estado === 'authorized';

  /**
   * Verifica si la suscripción está pendiente
   */
  const isPending = subscription?.estado === 'pending';

  /**
   * Verifica si la suscripción está pausada
   */
  const isPaused = subscription?.estado === 'paused';

  /**
   * Verifica si la suscripción está cancelada
   */
  const isCancelled = subscription?.estado === 'cancelled';

  /**
   * Obtiene los días hasta la próxima renovación
   */
  const getDaysUntilRenewal = useCallback(() => {
    if (!subscription?.proxima_renovacion) return null;
    
    const renewalDate = new Date(subscription.proxima_renovacion);
    const today = new Date();
    const diffTime = renewalDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  }, [subscription]);

  /**
   * Verifica si la suscripción se renueva pronto (en 7 días o menos)
   */
  const isRenewingSoon = useCallback(() => {
    const days = getDaysUntilRenewal();
    return days !== null && days <= 7 && days >= 0;
  }, [getDaysUntilRenewal]);

  /**
   * Obtiene el estado formateado
   */
  const getStatusDisplay = useCallback(() => {
    if (!subscription) return null;
    
    const statusMap = {
      pending: 'Pendiente',
      authorized: 'Activa',
      paused: 'Pausada',
      cancelled: 'Cancelada',
      expired: 'Expirada'
    };
    
    return statusMap[subscription.estado] || subscription.estado;
  }, [subscription]);

  // Efecto para cargar la suscripción
  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  // Efecto para auto-refresco
  useEffect(() => {
    if (!autoRefresh || !userId) return;

    const interval = setInterval(() => {
      loadSubscription();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, loadSubscription, userId]);

  return {
    // Datos
    subscription,
    loading,
    error,
    lastUpdated,
    
    // Estados derivados
    isActive,
    isPending,
    isPaused,
    isCancelled,
    isRenewingSoon,
    
    // Métodos
    refreshSubscription,
    cancelSubscription,
    pauseSubscription,
    reactivateSubscription,
    syncWithMercadoPago,
    getPaymentHistory,
    getDaysUntilRenewal,
    getStatusDisplay
  };
};

export default useSubscription;
